import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppState } from 'containers/ApplicationStateContext';

import useOnChange from './useOnChange';

export type UseTableSearchConfig = {
  storeKey?: string;
  initialSearchValue?: string;
  onSearch?: (searchTerm: string) => void;
  clearSearchRef: React.MutableRefObject<(() => void) | undefined>;
};

const useTableSearch = ({
  storeKey,
  initialSearchValue,
  onSearch,
  clearSearchRef,
}: UseTableSearchConfig) => {
  const [search, setSearch] = useState('');

  const { storedValues, setStoredValue } = useAppState();

  const key = useMemo(() => storeKey && `${storeKey}Search`, [storeKey]);

  const handleSearch = useCallback(
    (currentValue?: string) => {
      onSearch?.(currentValue ?? (key ? storedValues[key] : search) ?? '');
    },
    [storedValues, key, search, onSearch]
  );

  useEffect(() => {
    if (key) {
      setStoredValue({ key, value: initialSearchValue ?? '' });
    } else {
      setSearch(initialSearchValue ?? '');
    }
  }, [key, initialSearchValue, setStoredValue]);

  useOnChange(key ? storedValues[key] : search, (value) => {
    if (key) {
      setStoredValue({ key, value });
    } else {
      setSearch(value);
    }
  });

  useEffect(() => {
    if (!key && clearSearchRef && !clearSearchRef.current) {
      // TODO: is this still needed?
      clearSearchRef.current = () => setSearch('');
    }
  }, [key, setStoredValue, clearSearchRef]);

  return {
    search: (key ? storedValues[key] : search) ?? '',
    handleSearch,
    onChange: (value: any) => (key ? setStoredValue({ key, value }) : setSearch(value)),
  };
};

export default useTableSearch;
